import { useEffect, useMemo, useState } from "react";
import { gammaProfile, walls, flowSummary } from "../lib/optionsMath";
import { computeRegime } from "../lib/regime";

// «I dag»: hvordan er markedet, hva ligger foran oss, hvordan posisjonerer opsjonsmarkedet seg, og hvor går pengene.

const SECTORS = [["XLK", "Teknologi"], ["SMH", "Halvledere"], ["XLC", "Kommunikasjon"], ["XLY", "Forbruk (syklisk)"],
  ["XLP", "Forbruk (stabil)"], ["XLE", "Energi"], ["XLF", "Finans"], ["XLV", "Helse"], ["XLI", "Industri"],
  ["XLB", "Materialer"], ["XLU", "Kraft"], ["XLRE", "Eiendom"]];
const MKT = ["SPY", "QQQ", "RSP", "^VIX", "^VIX3M", "HYG", "LQD"];

const C = { bg: "#060f1c", bg2: "#0a1628", border: "#1e293b", dim: "#475569", text: "#94a3b8", bright: "#e2e8f0",
  green: "#10b981", yellow: "#f59e0b", red: "#f43f5e", blue: "#3b82f6" };
const LAMP = { 1: C.green, 0: C.yellow, "-1": C.red };
const card = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" };
const head = { fontSize: 10, color: C.dim, letterSpacing: 1, marginBottom: 10, fontWeight: 700 };
const fmtP = (v, d = 1) => (v == null ? "–" : `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`);
const fmtM = v => (v == null ? "–" : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${(v / 1e3).toFixed(0)}k`);
const isoDay = (offsetDays = 0) => new Date(Date.now() + offsetDays * 86400000).toLocaleDateString("en-CA");

async function getPerf(tickers) {
  const out = {};
  for (let i = 0; i < tickers.length; i += 40) {
    const r = await fetch(`/perf?t=${encodeURIComponent(tickers.slice(i, i + 40).join(","))}`);
    if (r.ok) Object.assign(out, await r.json());
  }
  return out;
}

function RelBar({ v, scale = 2.5 }) {
  const w = Math.min(50, Math.abs(v) * scale);
  return (
    <div style={{ position: "relative", height: 8, background: C.bg2, borderRadius: 4 }}>
      <div style={{ position: "absolute", top: 0, bottom: 0, left: v >= 0 ? "50%" : `${50 - w}%`, width: `${w}%`, background: v >= 0 ? C.green : C.red, borderRadius: 4 }} />
      <div style={{ position: "absolute", top: -2, bottom: -2, left: "50%", width: 1, background: "#334155" }} />
    </div>
  );
}

const LEVEL = { advarsel: ["⚠️", C.red], mulighet: ["🟢", C.green], info: ["ℹ️", C.text] };

export default function IDag({ tickers, favorites, calEvents, liveData, isMobile, onOpen, pin }) {
  const [perf, setPerf] = useState(null);
  const [scan, setScan] = useState(null);
  const [opt, setOpt] = useState({});            // ticker -> svar fra /options
  const [flowT, setFlowT] = useState("SPY");
  const local = window.location.hostname === "localhost";
  const tkKey = tickers.join(",");   // stabil avhengighet: lista lages på nytt ved hver tegning i App
  const perfList = useMemo(() => [...new Set([...MKT, ...SECTORS.map(s => s[0]), ...tkKey.split(",").filter(Boolean)])], [tkKey]);

  useEffect(() => {
    if (local) return;
    const load = () => getPerf(perfList).then(setPerf).catch(() => setPerf(p => p || {}));
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [perfList, local]);

  // Varsler fra den daglige skanneren (samme regler som push-varselet)
  useEffect(() => {
    if (local || !pin) return;
    fetch("/api/scan", { headers: { "X-Pin": pin } }).then(r => r.json()).then(setScan).catch(e => setScan({ error: e.message }));
  }, [pin, local]);

  useEffect(() => {
    if (local) return;
    for (const t of new Set(["SPY", flowT])) {
      if (opt[t]) continue;
      setOpt(p => ({ ...p, [t]: { loading: true } }));
      fetch(`/options?t=${encodeURIComponent(t)}`).then(r => r.json())
        .then(d => setOpt(p => ({ ...p, [t]: d.error ? { error: d.error } : d })))
        .catch(e => setOpt(p => ({ ...p, [t]: { error: e.message } })));
    }
  }, [flowT, local]); // eslint-disable-line react-hooks/exhaustive-deps

  const spy = opt.SPY?.contracts ? opt.SPY : null;
  const spyGamma = useMemo(() => (spy ? gammaProfile(spy.contracts, spy.currentPrice, spy.iv30) : null), [spy]);
  const spyWalls = useMemo(() => (spy ? walls(spy.contracts, spy.currentPrice) : null), [spy]);

  // ── Markedsregime (felles regler med den daglige skanneren)
  const regime = useMemo(() => computeRegime(perf, spyGamma), [perf, spyGamma]);

  // ── Utsikter: forventet bevegelse fra IV30, opsjonsvegger, hendelser og rapporter
  const move = spy?.iv30 ? { p: spy.currentPrice, iv: spy.iv30, w: spy.currentPrice * spy.iv30 * Math.sqrt(5 / 252), m: spy.currentPrice * spy.iv30 * Math.sqrt(21 / 252) } : null;
  const events = (calEvents || [])
    .map(e => ({ ...e, ds: new Date(e.date).toLocaleDateString("en-CA") }))
    .filter(e => e.ds >= isoDay(0) && e.ds <= isoDay(7) && e.cat !== "Earnings")
    .sort((a, b) => a.ds.localeCompare(b.ds)).slice(0, 6);
  const earnings = tickers.map(t => ({ t, d: liveData?.[t]?.nextEarnings, w: liveData?.[t]?.nextEarningsTime }))
    .filter(x => x.d && x.d >= isoDay(0) && x.d <= isoDay(14)).sort((a, b) => a.d.localeCompare(b.d)).slice(0, 8);

  // ── Opsjonsflyt for valgt ticker
  const fo = opt[flowT];
  const flow = useMemo(() => (fo?.contracts
    ? { ...flowSummary(fo.contracts, fo.currentPrice), g: gammaProfile(fo.contracts, fo.currentPrice, fo.iv30), w: walls(fo.contracts, fo.currentPrice) }
    : null), [fo]);
  const flowTickers = [...new Set(["SPY", "QQQ", ...favorites])].slice(0, 12);

  // ── Følg bølgen: sektorer og aksjer relativt til markedet
  const spyM1 = perf?.SPY?.m1;
  const sectors = spyM1 == null ? [] : SECTORS.filter(([t]) => perf[t]?.m1 != null).map(([t, n]) => ({ t, n, rel: perf[t].m1 - spyM1 })).sort((a, b) => b.rel - a.rel);
  const ranked = spyM1 == null ? [] : tickers.filter(t => perf[t]?.m1 != null)
    .map(t => ({ t, ...perf[t], rel: perf[t].m1 - spyM1, trend: !!(perf[t].ma50 && perf[t].ma200 && perf[t].price > perf[t].ma50 && perf[t].ma50 > perf[t].ma200) }))
    .sort((a, b) => b.rel - a.rel);
  const strong = ranked.filter(x => x.trend).slice(0, 8);
  const weak = ranked.filter(x => perf[x.t].ma50 && perf[x.t].price < perf[x.t].ma50).slice(-5).reverse();

  const grid2 = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 };

  if (local) return <div style={{ ...card, color: C.text, fontSize: 12 }}>«I dag» henter data via Cloudflare-funksjonene og virker bare på aiaksjer.pages.dev.</div>;

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", letterSpacing: 1, marginBottom: 4 }}>☀️ I DAG</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 16 }}>Markedet nå, hva som ligger foran oss, hvordan opsjonsmarkedet posisjonerer seg, og hvor pengene går.</div>

      {/* 0. Varsler */}
      <div style={{ ...card, marginBottom: 14, borderColor: scan?.alerts?.some(a => a.level === "advarsel") ? C.red + "60" : scan?.alerts?.some(a => a.level === "mulighet") ? C.green + "50" : C.border }}>
        <div style={head}>🔔 VARSLER · DET SOM ER VERDT Å SE PÅ</div>
        {!pin ? <div style={{ fontSize: 12, color: C.dim }}>Koble til sky (🔒-knappen øverst) for å få varsler om ledere, advarsler og rapporter for favorittene og posisjonene dine.</div>
          : !scan ? <div style={{ fontSize: 12, color: C.dim }}>Skanner …</div>
          : scan.error ? <div style={{ fontSize: 12, color: C.dim }}>Kunne ikke skanne: {scan.error}</div>
          : (() => {
            const list = scan.alerts.filter(a => !a.title.startsWith("Markedslyset:"));
            return list.length ? list.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: `1px solid ${C.bg2}`, alignItems: "baseline" }}>
                <span style={{ fontSize: 13 }}>{LEVEL[a.level][0]}</span>
                <div style={{ flex: 1 }}>
                  <span onClick={() => a.ticker && onOpen(a.ticker)} style={{ fontSize: 12, fontWeight: 700, color: LEVEL[a.level][1], cursor: a.ticker ? "pointer" : "default" }}>{a.title}</span>
                  <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5 }}>{a.body}</div>
                </div>
              </div>
            )) : <div style={{ fontSize: 12, color: C.dim }}>Ingenting spesielt i dag. Det er også et signal: ingen grunn til å handle bare for å handle.</div>;
          })()}
        <div style={{ fontSize: 10, color: "#334155", marginTop: 8, lineHeight: 1.5 }}>
          Skanner {scan?.scanned ?? "–"} aksjer: ledere i opptrend (testet), Bull Trap (testet advarsel), innsidekjøp i klynge (SEC Form 4 via OpenInsider), rapporter og åpne posisjoner.
          Push-varsel kommer hver handelsdag etter stengning når ntfy eller Telegram er satt opp.
        </div>
      </div>

      {/* 1. Markedsregime */}
      <div style={{ ...card, marginBottom: 14, borderColor: regime ? LAMP[regime.level] + "60" : C.border }}>
        <div style={head}>MARKEDET NÅ</div>
        {!regime ? <div style={{ fontSize: 12, color: C.dim }}>Henter markedsdata …</div> : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: LAMP[regime.level], boxShadow: `0 0 14px ${LAMP[regime.level]}` }} />
              <div style={{ fontSize: 18, fontWeight: 800, color: LAMP[regime.level], letterSpacing: 1 }}>{regime.title}</div>
              <div style={{ fontSize: 12, color: C.text }}>{regime.hint}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 8 }}>
              {regime.items.map(it => (
                <div key={it.label} style={{ background: C.bg2, borderRadius: 6, padding: "8px 10px", borderLeft: `3px solid ${LAMP[it.v]}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontSize: 11, color: C.text }}>{it.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: LAMP[it.v] }}>{it.val}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 3, lineHeight: 1.5 }}>{it.why}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={grid2}>
        {/* 2. Utsikter */}
        <div style={card}>
          <div style={head}>FREMOVER</div>
          {move ? (
            <div style={{ fontSize: 12, color: C.text, lineHeight: 1.8, marginBottom: 10 }}>
              Opsjonene priser S&P 500 (SPY ${move.p.toFixed(0)}) til å bevege seg <b style={{ color: C.bright }}>±{(move.w / move.p * 100).toFixed(1)} %</b> neste uke
              {" "}og <b style={{ color: C.bright }}>±{(move.m / move.p * 100).toFixed(1)} %</b> neste måned
              <span style={{ color: C.dim }}> (68 % sannsynlighet, IV30 {(move.iv * 100).toFixed(0)} %)</span>.
              <div style={{ fontSize: 11, color: C.dim }}>Månedsintervall: ${(move.p - move.m).toFixed(0)} – ${(move.p + move.m).toFixed(0)}</div>
            </div>
          ) : <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Henter opsjonsdata for SPY …</div>}
          {spyWalls && spyGamma && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
              {[["Motstand (call-vegg)", spyWalls.callWall?.strike, C.red], ["Zero gamma", spyGamma.zeroGamma, C.yellow], ["Støtte (put-vegg)", spyWalls.putWall?.strike, C.green]].map(([l, v, c]) => (
                <div key={l} style={{ background: C.bg2, borderRadius: 6, padding: "6px 8px" }}>
                  <div style={{ fontSize: 9, color: C.dim }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{v != null ? `$${v.toFixed(0)}` : "–"}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, marginBottom: 4 }}>HENDELSER NESTE 7 DAGER</div>
          {events.length ? events.map((e, i) => (
            <div key={i} style={{ fontSize: 11, color: C.text, padding: "2px 0" }}>
              <span style={{ color: e.color || C.yellow, fontWeight: 700, display: "inline-block", width: 74 }}>{new Date(e.date).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" })}</span>{e.title}
            </div>
          )) : <div style={{ fontSize: 11, color: C.dim }}>Ingen store makrohendelser</div>}
          <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, margin: "10px 0 4px" }}>RAPPORTER I DINE AKSJER (14 DAGER)</div>
          {earnings.length ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {earnings.map(x => (
                <span key={x.t} onClick={() => onOpen(x.t)} style={{ fontSize: 11, padding: "3px 8px", background: C.bg2, border: `1px solid ${C.yellow}40`, borderRadius: 4, color: C.yellow, cursor: "pointer" }}>
                  {x.t} <span style={{ color: C.dim }}>{new Date(x.d + "T12:00:00").toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}{x.w ? ` ${x.w}` : ""}</span>
                </span>
              ))}
            </div>
          ) : <div style={{ fontSize: 11, color: C.dim }}>Ingen kjente rapporter (datoer hentes når kursene er lastet)</div>}
        </div>

        {/* 3. Opsjonsflyt */}
        <div style={card}>
          <div style={head}>OPSJONSFLYT I DAG · HVORDAN POSISJONERER FOLK SEG</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
            {flowTickers.map(t => (
              <button key={t} onClick={() => setFlowT(t)}
                style={{ padding: "3px 9px", fontSize: 10, fontFamily: "inherit", cursor: "pointer", borderRadius: 4, border: "1px solid",
                  borderColor: flowT === t ? C.blue : C.border, background: flowT === t ? C.blue + "20" : "transparent", color: flowT === t ? "#93c5fd" : C.dim }}>{t}</button>
            ))}
          </div>
          {!flow ? <div style={{ fontSize: 12, color: C.dim }}>{fo?.error ? `Ingen opsjonsdata for ${flowT}` : "Henter …"}</div> : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                <span style={{ color: C.green }}>Calls {fmtM(flow.callPrem)}</span>
                <span style={{ color: C.text }}>P/C volum {flow.pcVol?.toFixed(2) ?? "–"}</span>
                <span style={{ color: C.red }}>Puts {fmtM(flow.putPrem)}</span>
              </div>
              <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginBottom: 10 }}>
                <div style={{ width: `${(flow.callShare ?? 0.5) * 100}%`, background: C.green }} />
                <div style={{ flex: 1, background: C.red }} />
              </div>
              <div style={{ fontSize: 11, color: C.text, marginBottom: 8, lineHeight: 1.6 }}>
                {flow.callShare != null && (flow.callShare > 0.6 ? "Overvekt av call-premie: optimisme." : flow.callShare < 0.4 ? "Overvekt av put-premie: sikring eller pessimisme." : "Balansert flyt.")}
                {flow.g && <> Dealer-gamma <b style={{ color: flow.g.positive ? C.green : C.red }}>{flow.g.positive ? "positiv" : "negativ"}</b>{flow.g.zeroGamma ? `, zero gamma $${flow.g.zeroGamma.toFixed(2)}` : ""}.</>}
                {flow.w?.callWall && <> Call-vegg ${flow.w.callWall.strike}</>}{flow.w?.putWall && <>, put-vegg ${flow.w.putWall.strike}.</>}
              </div>
              <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, marginBottom: 4 }}>STØRSTE NYE POSISJONER (volum over åpne kontrakter)</div>
              {flow.unusual.length ? flow.unusual.map((u, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "48px 1fr 70px 60px", gap: 6, fontSize: 11, padding: "2px 0", color: C.text }}>
                  <span style={{ color: u.type === "C" ? C.green : C.red, fontWeight: 700 }}>{u.type === "C" ? "CALL" : "PUT"}</span>
                  <span>${u.strike} · {new Date(u.expiration + "T12:00:00").toLocaleDateString("nb-NO", { day: "numeric", month: "short" })} <span style={{ color: C.dim }}>({u.otm >= 0 ? `${u.otm.toFixed(0)} % OTM` : "ITM"})</span></span>
                  <span style={{ textAlign: "right" }}>{fmtM(u.prem)}</span>
                  <span style={{ textAlign: "right", color: C.dim }}>{u.ratio.toFixed(1)}× OI</span>
                </div>
              )) : <div style={{ fontSize: 11, color: C.dim }}>Ingen store nye posisjoner i dag</div>}
              <div style={{ fontSize: 9, color: "#334155", marginTop: 8, lineHeight: 1.5 }}>
                CBOE-data, 15 min forsinket. Utløp i dag og i morgen er holdt utenfor (dagshandel). Vi ser volum, ikke om handelen var kjøp eller salg, så «call-premie» kan også være noen som selger calls.
              </div>
            </>
          )}
        </div>
      </div>

      {/* 4. Følg bølgen */}
      <div style={grid2}>
        <div style={card}>
          <div style={head}>SEKTORROTASJON · 1 MÅNED MOT S&P 500</div>
          {sectors.length ? sectors.map(s => (
            <div key={s.t} style={{ display: "grid", gridTemplateColumns: isMobile ? "110px 1fr 52px" : "150px 1fr 60px", gap: 8, alignItems: "center", padding: "2px 0" }}>
              <span style={{ fontSize: 11, color: C.text }}>{s.n} <span style={{ color: C.dim }}>{s.t}</span></span>
              <RelBar v={s.rel} />
              <span style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color: s.rel >= 0 ? C.green : C.red }}>{fmtP(s.rel)}</span>
            </div>
          )) : <div style={{ fontSize: 12, color: C.dim }}>Henter …</div>}
        </div>
        <div style={card}>
          <div style={head}>FØLG BØLGEN · DINE STERKESTE AKSJER</div>
          <div style={{ fontSize: 10, color: C.dim, marginBottom: 8, lineHeight: 1.5 }}>Opptrend (kurs &gt; 50d &gt; 200d), sortert etter styrke mot S&P 500 siste måned. Det er her institusjonene kjøper.</div>
          {strong.length ? strong.map(x => (
            <div key={x.t} onClick={() => onOpen(x.t)} style={{ display: "grid", gridTemplateColumns: "60px 1fr 60px 60px", gap: 8, alignItems: "center", padding: "3px 0", cursor: "pointer" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.bright }}>{x.t}</span>
              <RelBar v={x.rel} scale={1.5} />
              <span style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color: C.green }}>{fmtP(x.rel)}</span>
              <span style={{ fontSize: 10, textAlign: "right", color: C.dim }}>{fmtP(x.fromHigh)} fra topp</span>
            </div>
          )) : <div style={{ fontSize: 12, color: C.dim }}>{perf ? "Ingen aksjer i tydelig opptrend nå" : "Henter …"}</div>}
          {weak.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, margin: "10px 0 4px" }}>SVAKEST (under 50-dagers snitt) · unngå eller vær forsiktig</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {weak.map(x => (
                  <span key={x.t} onClick={() => onOpen(x.t)} style={{ fontSize: 11, padding: "3px 8px", background: C.bg2, border: `1px solid ${C.red}40`, borderRadius: 4, color: C.red, cursor: "pointer" }}>
                    {x.t} {fmtP(x.rel)}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ fontSize: 10, color: "#334155", lineHeight: 1.6 }}>
        Markedslyset er tommelfingerregler som mange profesjonelle følger, ikke en backtestet modell. Ikke finansiell rådgivning.
      </div>
    </div>
  );
}
